<template>
  <app-client-layout
    v-bind="flashObject"
    title="体験模試">
    <template #header>
      <h2 class="text-xl font-semibold leading-tight text-gray-800">
        模試の受験/購入
      </h2>
    </template>
    <p
      v-if="$page.props.flash.message"
      class="text-red-400 text-lg font-black mt-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      {{ $page.props.flash.message }}
    </p>

    <div class="mx-auto">
      <div class="border-2 border-double border-red-500 bg-white p-4 rounded-lg max-w-screen-md mx-auto my-6">
        <div class="flex items-center justify-center">
          <img
            style="width: 40px; height: 40px; opacity: 1"
            :src="logoUrl">
          <span class="ml-2 text-red-500 font-bold">模試受験における注意事項</span>
        </div>
        <ul class="mt-4 text-red-500 text-sm font-bold pb-2">
          <li>・試験は一度しか受験できません</li>
          <li class="my-2">・制限時間が経過すると強制的に試験終了となります</li>
          <li>・十分な時間を確保し実施してください</li>
        </ul>
      </div>
    </div>

    <div class="md:flex justify-center mx-auto">
      <!-- 模試一覧 -->
      <ExamList
        :examinations="examinations"
        :selected-examination-index="selectedExaminationIndex"
        :remaining-purchase-ticket="userData.remaining_purchase_ticket"
        :show-ticket-count="true"
        @select-examination="selectExamination" />
      <div class="col-span-8 flex-1 max-w-2xl m-5 text-gray-600 body-font">
        <!-- まとめ買い -->
        <SetPurchase
          v-if="getBulkPurchaseForExam(examinations[selectedExaminationIndex].id)"
          :selected-exam-id="examinations[selectedExaminationIndex].id"
          :selected-examination="examinations[selectedExaminationIndex]"
          :examinations="examinations"
          :purchases="purchases"
          :bulk-purchase="getBulkPurchaseForExam(examinations[selectedExaminationIndex].id)"
          @purchase-ticket="purchaseTicket" />
        <!-- 模試詳細 -->
        <ExamDetail
          :examination="examinations[selectedExaminationIndex]"
          :examination-types="examinationTypes"
          :examination-types-spec="examinationTypesSpec"
          :purchases="purchases"
          :progresses="progresses"
          :user-data="userData"
          :limit-times="limitTimes"
          :fiscal-year-start="fiscalYearStart"
          @purchase-exam="purchaseExam"
          @confirm="confirm"
          @resume="resume" />
      </div>
    </div>
  </app-client-layout>
</template>

<script setup>
import moment from 'moment'
import Swal from 'sweetalert2'
import { Inertia } from '@inertiajs/inertia'
import {
  computed, reactive, ref, onMounted,
} from 'vue'
import AppClientLayout from '@/Layouts/AppClientLayout'

import ExamList from './ExamList.vue'
import SetPurchase from './SetPurchase.vue'
import ExamDetail from './ExamDetail.vue'

const props = defineProps({
  examinations: {
    type: Array,
    default: () => [],
  },
  examination: {
    type: Object,
    default: () => { },
  },
  examinationTypes: {
    type: Object,
    default: () => { },
  },
  examinationTypesSpec: {
    type: Object,
    default: () => { },
  },
  purchases: {
    type: Array,
    default: () => [],
  },
  progresses: {
    type: Array,
    default: () => [],
  },
  startAnswerTime: {
    type: String,
    default: '',
  },
  limitTimes: {
    type: Object,
    default: () => { },
  },
  userData: {
    type: Object,
    default: () => ({}),
  },
  flash: {
    type: Object,
    default: () => ({}),
  },
  fiscalYearStart: {
    type: String,
    required: true,
  },
})

// 注意事項表示ブロックのロゴURL
const logoUrl = '/images/Exam/warning-icon.svg'

onMounted(() => {
  for (const [index, examination] of props.examinations.entries()) {
    if (isWithinPeriodExam(examination) && examination.is_published) {
      selectedExaminationIndex.value = index
      break
    }
  }
})

const exam = ref()
const examType = ref()

const show = reactive({})

const selectedExaminationIndex = ref(0)

const selectExamination = (index) => {
  selectedExaminationIndex.value = index
}

const flashObject = computed(() => ({
  flash: props.flash,
}))

const isOutOfTerm = (id) => {
  const examObj = props.examinations.find(
    (examination) => examination.id === id,
  )
  const now = moment()
  const receptionStart = moment(examObj.reception_start)
  const receptionEnd = moment(examObj.reception_end)
  if (now.isBefore(receptionStart) || now.isAfter(receptionEnd)) {
    return true
  }
  return false
}

const isShowWarning = (id) => {
  const examObj = props.examinations.find(
    (examination) => examination.id === id,
  )
  const now = moment()
  const receptionEnd = moment(examObj.reception_end)
  if (now.diff(receptionEnd, 'days') > -1) {
    return true
  }
  return false
}

const getBulkPurchaseForExam = (id) => {
  const examination = props.examinations.find(exam => exam.id === id)
  if (examination && examination.bulk_purchases && examination.bulk_purchases.length > 0) {
    return examination.bulk_purchases[0] // 最初のまとめ買い情報を返す
  }
  return null
}

// チケット購入処理
const purchaseTicket = (bulkPurchase) => {
  // ユーザ情報が入力されていなければユーザ情報入力用のモーダルを表示する
  if (isBlankUserInfo()) {
    showRequiredUserInputModal()
  } else {
    // まとめ買いは学割なしで直接購入画面へ
    Inertia.post(route('purchase-ticket'), {
      bulk_purchase_id: bulkPurchase.id,
      selected_examination_id: props.examinations[selectedExaminationIndex.value].id,
    })
  }
}

const purchaseExam = (examination) => {
  exam.value = examination

  if (isOutOfTerm(examination.id)) {
    Swal.fire({
      title: '購入期間外です。',
      icon: 'warning',
      confirmButtonText: '閉じる',
    })
    return
  }
  if (isShowWarning(examination.id)) {
    Swal.fire({
      icon: 'warning',
      title: '実施終了日時が近づいています。',
      html: '実施終了日時までに<br>試験開始できなかった模試種別は<br>受験できませんのでご注意ください。<br>購入画面に進みますか？',
      showCancelButton: true,
      confirmButtonText: 'はい',
      cancelButtonText: 'いいえ',
    }).then((result) => {
      if (!result.isConfirmed) {

      }
    })
  }

  if (isBlankUserInfo()) {
    showRequiredUserInputModal()
  } else if (examination.is_subscription_item) {
    Inertia.post(
      route('store', {
        examination: examination.id,
      }),
    )
  } else if (props.userData.remaining_purchase_ticket == 0) {
    if (isOutOfTerm(2)) {
      // 第二回目模試が終わったらチケット販売を終了する。
      showPurchaseTypeModal(false)
    } else {
      // チケットがない場合
      Swal.fire({
        icon: 'warning',
        title: 'チケットをまとめて購入した方がお安くお買い求めいただけます。',
        text: '単体で購入してもよろしいですか？',
        showCancelButton: true,
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
      }).then((result) => {
        if (result.isConfirmed) {
          showPurchaseTypeModal(false)
        }
      })
    }
  } else {
    Swal.fire({
      icon: 'question',
      title: 'チケットを消費して模試を購入します。',
      text: 'この操作は取り消せません。よろしいですか？',
      showCancelButton: true,
      confirmButtonText: 'はい',
      cancelButtonText: 'いいえ',
    }).then((result) => {
      if (result.isConfirmed) {
        Inertia.post(
          route('purchase-use-ticket', {
            exam_id: exam.value.id,
          }),
        )
      }
    })
  }
}

const showRequiredUserInputModal = () => {
  Swal.fire({
    icon: 'warning',
    title: 'ユーザ情報を入力してください',
    showCancelButton: true,
    confirmButtonText: '進む',
    cancelButtonText: '戻る',
  }).then((result) => {
    if (result.isConfirmed) {
      Inertia.get(route('client.profile.show'))
    }
  })
}

// showPurchaseTypeModal を簡素化（学割選択ダイアログ削除）
const showPurchaseTypeModal = (isTicket) => {
  // 学割機能を削除したため、直接購入処理に進む
  if (isTicket) {
    const bulkPurchase = getBulkPurchaseForExam(exam.value.id)
    if (bulkPurchase) {
      Inertia.post(route('purchase-ticket'), {
        bulk_purchase_id: bulkPurchase.id,
        selected_examination_id: exam.value.id,
      })
    }
  } else {
    Inertia.get(
      route('purchase', {
        examination: exam.value.id,
      }),
    )
  }
}

const isBlankUserInfo = () => {
  let isBlank = false
  // 学生情報が入力される場合は基本情報を必須にしない
  if (!props.userData.is_input_school_info) {
    if (
      !props.userData.name
      || props.userData.sex == null
      || !props.userData.postal_code
      || String(props.userData.postal_code).length < 7
      || !props.userData.address
      || !props.userData.job
      || !props.userData.service_years
    ) {
      isBlank = true
    } else if (props.userData.job === 13 && !props.userData.job_text) {
      isBlank = true
    } else if (
      props.userData.service_years === 10
      && !props.userData.service_years_text
    ) {
      isBlank = true
    }
  }
  // 学生情報が有効な場合は学生情報の必須チェック
  if (
    props.userData.is_input_school_info
    && (!props.userData.name
      || !props.userData.service_years
      || !props.userData.school
      || !props.userData.department
      || !props.userData.school_email
      || !props.userData.student_id)
  ) {
    isBlank = true
  }

  return isBlank
}

const resume = (examination, type) => {
  const json = localStorage.getItem('answers')
  const array = json != null ? JSON.parse(json) : []

  const form = Inertia.form({
    answerList: array,
    examination,
    examination_type: type,
  })
  form.post('/play')

  show.value = false
}

const isInProgress = (id, type) => props.progresses.filter(
  (progress) => progress.examination_id === id
      && progress.examination_type === type
      && progress.started_at !== null
      && progress.finished_at === null,
).length

const play = (examination, type) => {
  localStorage.clear()
  const form = Inertia.form({
    examination,
    examination_type: type,
  })
  form.post('/play')
}

const confirm = (examination, type) => {
  let isProgress = false
  switch (type) {
    case 1:
      isProgress = isInProgress(examination.id, 2) || isInProgress(examination.id, 3)
      break
    case 2:
      isProgress = isInProgress(examination.id, 1) || isInProgress(examination.id, 3)
      break
    case 3:
      isProgress = isInProgress(examination.id, 1) || isInProgress(examination.id, 2)
      break
    default:
      isProgress = false
      break
  }

  if (isProgress) {
    Swal.fire({
      title: '他の試験を実施中です。',
      text: '先に実施中の試験を終了させてください。',
      icon: 'warning',
      confirmButtonText: '閉じる',
    })
    return
  }
  exam.value = examination
  examType.value = type

  Swal.fire({
    icon: 'question',
    text: '試験を開始してもよろしいですか？',
    showCancelButton: true,
    confirmButtonText: 'はい',
    cancelButtonText: 'いいえ',
  }).then((result) => {
    if (result.isConfirmed) {
      play(exam, examType)
    }
  })
}

window.history.pushState(null, null, null)
window.addEventListener('popstate', () => {
  window.history.pushState(null, null, null)
})

const isWithinPeriodExam = (examination) => {
  const now = moment()
  return now.isBefore(examination.reception_end)
}
</script>
